import type { ContractPdfData, PdfFieldMapping } from "../../types/pdf";

/**
 * Maps customer and contract data to PDF form field structure
 * based on the JSON field definitions for Gas and Strom contracts
 */
export function mapContractDataToPdfFields(
  data: ContractPdfData
): PdfFieldMapping {
  const { customer, bankDetails, selectedDate } = data;

  // Format address components
  const streetAndNumber = `${customer.address.street} ${customer.address.houseNumber}`.trim();
  const zipAndCity = `${customer.address.zipCode} ${customer.address.city}`.trim();

  // Map to PDF field structure
  const fieldMapping: PdfFieldMapping = {
    "1_Kunde": {
      Vorname: customer.firstName || "",
      Name: customer.lastName || "",
      Geburtsdatum: customer.birthdate || "",
      "E-Mail": customer.email || "",
      "Straße/Hausnummer": streetAndNumber,
      "PLZ/Ort": zipAndCity,
      Telefon_privat: customer.phone || "",
    },
    "8_Erteilung_eines_SEPA-Lastschriftmandats": {
      "Name_Vorname_(Kontoinhaber)": `${customer.firstName} ${customer.lastName}`.trim(),
      "Straße_Nr_(Kontoinhaber)": streetAndNumber,
      "PLZ/Ort_(Kontoinhaber)": zipAndCity,
      IBAN: bankDetails || "",
    },
    "10_Auftragserteilung": {
      "Ort/Datum": `${customer.address.city}, ${selectedDate}`.trim(),
      Unterschrift_Kunde: data.signatureBase64 || "", // Fallback to empty string if signature not available
    },
  };

  return fieldMapping;
}

/**
 * Flattens the nested field mapping structure for easier iteration
 * Returns an array of { fieldName, value } objects
 */
export function flattenPdfFields(
  mapping: PdfFieldMapping
): Array<{ fieldName: string; value: string }> {
  const flatFields: Array<{ fieldName: string; value: string }> = [];

  for (const [, fields] of Object.entries(mapping)) {
    if (typeof fields === "object" && fields !== null) {
      for (const [fieldName, value] of Object.entries(fields)) {
        // Skip nested objects and arrays, only process string values
        if (
          typeof value === "string" &&
          fieldName !== "Unterschrift_Kunde" // Signature handled separately
        ) {
          flatFields.push({ fieldName, value });
        }
      }
    }
  }

  return flatFields;
}

/**
 * Formats a date to German format (dd.mm.yyyy)
 * Handles various input formats
 */
export function formatDateForPdf(dateString: string): string {
  try {
    if (!dateString) return "";

    // Allow non-date strings (like "Schnellstmöglich") to pass through unchanged
    if (!/^\d/.test(dateString)) return dateString;

    // Check if already in dd.mm.yyyy format
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
      const parts = dateString.split(".");
      if (parts.length !== 3) return dateString;

      const day = parts[0] || "";
      const month = parts[1] || "";
      const year = parts[2] || "";

      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
    }

    // Parse from ISO format or other standard formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date for PDF:", error);
    return "";
  }
}

/**
 * Formats a date to German format with short year (dd.mm.yy)
 * Handles various input formats
 */
export function formatDateForPdfShortYear(dateString: string): string {
  try {
    if (!dateString) return "";

    // Allow non-date strings (like "Schnellstmöglich") to pass through unchanged
    if (!/^\d/.test(dateString)) return dateString;

    // Check if already in dd.mm.yy format (2-digit year)
    if (/^\d{1,2}\.\d{1,2}\.\d{2}$/.test(dateString)) {
      const parts = dateString.split(".");
      if (parts.length !== 3) return dateString;

      const day = parts[0] || "";
      const month = parts[1] || "";
      const year = parts[2] || "";

      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${year}`;
    }

    // Check if in dd.mm.yyyy format (4-digit year) and convert to 2-digit
    if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) {
      const parts = dateString.split(".");
      if (parts.length !== 3) return dateString;

      const day = parts[0] || "";
      const month = parts[1] || "";
      const year = parts[2] || "";

      // Take last 2 digits of year
      const shortYear = year.slice(-2);
      return `${day.padStart(2, "0")}.${month.padStart(2, "0")}.${shortYear}`;
    }

    // Parse from ISO format or other standard formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date for PDF with short year:", error);
    return "";
  }
}

/**
 * Gets the current date in German format (dd.mm.yyyy)
 */
export function getCurrentDateFormatted(): string {
  const date = new Date();
  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
